import { AsyncPipe } from '@angular/common';
import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { WorkplaceDetailAddressesStore } from './workplace-detail-addresses.store';
import { FilterInput } from '@it2go/data-grid';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FormBuilder, Validators } from '@angular/forms';
import { AddressDetailObject, WorkplaceAddressGridItemObject } from '@it2go/types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { isInvalid } from '../../../../shared/helpers/validate';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { mapAddress } from '../../../../shared/helpers/map-address.helper';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { addressColumn } from '../../../../shared/components/kit/table/table.fields';
import { CardModule } from '@openng/optimus-ui/card';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import {
    TextAreaInputComponent,
} from '../../../../../components/kit/input/text-area-input/text-area-input.component';

@Component({
    selector: 'app-workplace-detail-addresses',
    templateUrl: './workplace-detail-addresses.component.html',
    providers: [WorkplaceDetailAddressesStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AddressInputComponent,
        AsyncPipe,
        CardModule,
        DateInputComponent,
        DialogModule,
        PrimeTemplate,
        TableComponent,
        TextAreaInputComponent,
        TranslatePipe,
    ],
})
export class WorkplaceDetailAddressesComponent extends SubscriptionComponent {

    protected readonly store = inject(WorkplaceDetailAddressesStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);

    workplaceId: number | null = null;
    addressesAdditionalFilter: FilterInput[][] = [];

    form = this.fb.group({
        id: this.fb.control<number | null>(null),
        validFrom: this.fb.control<Date>(new Date(), Validators.required),
        address: this.fb.control<AddressDetailObject | null>(null, Validators.required),
        note: this.fb.control<string | null>(null),
    })

    modalVisible = false;

    columns: TableColumn[] = [
        {
            ...addressColumn,
            filterable: undefined,
            sortable: false,
        },
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
        {
            name: 'note',
        },
    ];

    @ViewChild('addressesTable') addressesTable!: TableComponent;

    constructor() {
        super();

        this.store.doOnActions(
            () => this.addressesTable.reload(),
            'createWorkplaceAddress',
            'updateWorkplaceAddress',
            'deleteWorkplaceAddress',
        )

        this.store.registerSuccessMessage('createWorkplaceAddress', 'common.created');
        this.store.registerSuccessMessage('updateWorkplaceAddress', 'common.updated');
        this.store.registerSuccessMessage('deleteWorkplaceAddress', 'common.deleted');

        this.subs.push(
            this.store.routeParam.workplaceId.subscribe((workplaceId) => {
                if (workplaceId) {
                    this.workplaceId = Number(workplaceId);
                    this.addressesAdditionalFilter = [[{ column: 'workplaceId', operator: 'EQ', value: [workplaceId] }]];
                }
            }),
        )
    }

    openCreateAddress() {
        this.modalVisible = true;
    }

    openEditAddress(address: WorkplaceAddressGridItemObject) {
        this.form.patchValue({
            id: address.id,
            validFrom: address.validFrom ? new Date(address.validFrom) : new Date(),
            address: address.address,
            note: address.note,
        });
        this.modalVisible = true;
    }

    closeModal() {
        this.modalVisible = false;
        this.form.reset();
    }

    submit() {
        if (isInvalid(this.form)) return;

        const data = {
            address: mapAddress(this.form.controls.address.value! as AddressDetailObject),
            validFrom: this.form.controls.validFrom.value!.toISOString(),
            note: this.form.controls.note.value || null,
        }

        if (this.form.controls.id.value) {
            this.store.api.updateWorkplaceAddress({
                id: this.form.controls.id.value,
                ...data,
            });
        } else {
            this.store.api.createWorkplaceAddress({
                workplaceId: this.workplaceId!,
                ...data,
            });
        }
        this.closeModal();
    }

    delete(item: any) {
        this.confirmBuilder.confirm(
            'workplace.addresses.deleteAddressConfirmation.header',
            'workplace.addresses.deleteAddressConfirmation.label',
            () => {
                this.store.api.deleteWorkplaceAddress({ id: item.item.id });
            },
            { name: 'workplaceAddressDeleteConfirm' + item.item.id },
            'pi-trash',
            item.event.target,
        );
    }
}
